import * as THREE from "three";
import { gsap } from "gsap";

export interface DotsOptions {
	/** 点的密度 */
	density?: number;
	/** 点的半径 */
	dotRadius?: number;
	/** 球体半径 */
	sphereRadius?: number;
	/** 地图图片路径 */
	mapImageUrl?: string;
	/** 材质配置 */
	material?: {
		/** 颜色A - 渐变起始色 */
		colorA?: {
			r: number;
			g: number;
			b: number;
		};
		/** 颜色B - 渐变结束色 */
		colorB?: {
			r: number;
			g: number;
			b: number;
		};
	};
}

export class Dots {
	/** 材质数组 */
	private materials: THREE.ShaderMaterial[] = [];
	/** 基础材质 */
	private baseMaterial: THREE.ShaderMaterial;
	/** 纬度经度映射 */
	private activeLatLon: Map<number, number[]> = new Map();
	/** 配置 */
	private readonly options: Required<DotsOptions>;

	/** 默认配置 */
	private static readonly DEFAULT_OPTIONS: Required<DotsOptions> = {
		/** 点的密度 */
		density: 2.5,
		/** 点的半径 */
		dotRadius: 0.1,
		/** 球体半径 */
		sphereRadius: 19.5,
		/** 地图图片路径 */
		mapImageUrl: '/textures/world_alpha_mini.png',
		/** 材质配置 */
		material: {
			/** 颜色A - 渐变起始色 */
			colorA: {
				r: 0.196,
				g: 0.631,
				b: 0.886
			},
			/** 颜色B - 渐变结束色 */
			colorB: {
				r: 0.192,
				g: 0.384,
				b: 0.498
			}
		}
	};

	/** 顶点着色器 */
	private static readonly VERTEX_SHADER = `
		#ifdef GL_ES
		precision mediump float;
		#endif

		uniform float u_time;
		uniform float u_maxExtrusion;

		void main() {
			vec3 newPosition = position;
			if(u_maxExtrusion > 1.0) {
				newPosition.xyz = newPosition.xyz * u_maxExtrusion + sin(u_time);
			} else {
				newPosition.xyz = newPosition.xyz * u_maxExtrusion;
			}
			gl_Position = projectionMatrix * modelViewMatrix * vec4(newPosition, 1.0);
		}
	`;

	/** 片元着色器 */
	private static readonly FRAGMENT_SHADER = `
		#ifdef GL_ES
		precision mediump float;
		#endif

		uniform float u_time;
		uniform vec3 u_colorA;
		uniform vec3 u_colorB;

		void main() {
			vec3 color = vec3(0.0);
			float pct = abs(sin(u_time));
			color = mix(u_colorA, u_colorB, pct);
			gl_FragColor = vec4(color, 1.0);
		}
	`;

	constructor(
		private readonly scene: THREE.Scene,
		options: DotsOptions = {}
	) {
		this.options = { ...Dots.DEFAULT_OPTIONS, ...options };
		this.baseMaterial = this.createBaseMaterial();
	}

	/**
	 * 初始化点状地图
	 */
	async init(): Promise<void> {
		try {
			const imageData = await this.loadMapImage(this.options.mapImageUrl);

			this.processImageData(imageData);
			this.createDots();
		} catch (error) {
			console.error('Failed to initialize dots:', error);
		}
	}

	/**
	 * 创建基础材质
	 */
	private createBaseMaterial(): THREE.ShaderMaterial {
		return new THREE.ShaderMaterial({
			side: THREE.DoubleSide,
			uniforms: {
				u_time: { value: 1.0 },
				u_maxExtrusion: { value: 1.0 },
				u_colorA: {
					value: new THREE.Vector3(
						this.options.material.colorA?.r || 0.196	,
						this.options.material.colorA?.g || 0.631,
						this.options.material.colorA?.b || 0.886
					)
				},
				u_colorB: {
					value: new THREE.Vector3(
						this.options.material.colorB?.r || 0.192,
						this.options.material.colorB?.g || 0.384,
						this.options.material.colorB?.b || 0.498
					)
				}
			},
			vertexShader: Dots.VERTEX_SHADER,
			fragmentShader: Dots.FRAGMENT_SHADER,
		});
	}

	/**
	 * 加载地图图片
	 */
	private loadMapImage(url: string): Promise<ImageData> {
		return new Promise((resolve, reject) => {
			const image = new Image();

			image.onload = () => {
				const canvas = document.createElement('canvas');
				canvas.width = image.width;
				canvas.height = image.height;

				const context = canvas.getContext('2d');
				if (!context) {
					reject(new Error('Failed to get 2D context'));
					return;
				}

				context.drawImage(image, 0, 0);
				resolve(context.getImageData(0, 0, canvas.width, canvas.height));
			};

			image.onerror = () => reject(new Error('Failed to load image'));
			image.src = url;
		});
	}

	/**
	 * 处理图片数据，其算法取决于地球贴图 texture 的起点与终点，这个循环的目的是遍历世界地图图片的每个像素，将其转换为地球表面的经纬度坐标。
   *  1. 从北极(90°) 开始，从西 (-180°) 到东(180°) 即**从左到右**扫描每一行，逐渐向南极 (-90°) 移动即**从上到下**。
   *  2. 这样可以将平面地图上的像素数据映射到球体表面上，创建点状地球效果。
	 */
	private processImageData(imageData: ImageData): void {
    /**
     * i = 0: 初始化索引 i，用于遍历图片像素数据数组
     * lon = -180: 初始化经度值，从 -180 度开始（代表地球最西端即西经 -180° W）
     * lat = 90: 初始化纬度值，从 90 度开始（代表北极 90° N）
     * i < imageData.data.length: 循环继续的条件，直到遍历完所有像素数据
     * i += 4: 每次递增 4，因为图片数据中每个像素占用4个值 (R,G,B,A)
     * lon++: 每处理完一个像素，经度值增加 1 度
     */
		for (let i = 0, lon = -180, lat = 90; i < imageData.data.length; i += 4, lon++) {
      // 如果当前纬度没有对应的经度，则创建一个空数组
			if (!this.activeLatLon.has(lat)) {
				this.activeLatLon.set(lat, []);
			}

      // 读取当前像素的 RGB 值
			const [r, g, b] = [
				imageData.data[i],
				imageData.data[i + 1],
				imageData.data[i + 2]
			];

      // 如果当前像素的 RGB 值都小于 80，则认为该位置是陆地，将经度添加到当前纬度的数组中
			if (r < 80 && g < 80 && b < 80) {
				this.activeLatLon.get(lat)?.push(lon);
			}

      // 如果经度达到 180，则将经度重置为 -180，并将纬度减 1
			if (lon === 180) {
        // 重置经度回到最西端，即回到最左边
				lon = -180;
        // 纬度减 1，即向南移动一格（也就是向下移动一格）
				lat--;
			}
		}
	}

	/**
	 * 创建点
	 */
	private createDots(): void {
    // 从北极 90° N 开始往往南极 -90° S 绘制
		for (let lat = 90, i = 0; lat > -90; lat--, i++) {
      /**
       * 计算当前纬度的半径，公式原理解析
       *
       * 1. 纬度与地球半径的关系
       *  地球不是一个完美的球体，而是一个略微扁平的椭球体。不同纬度上的半径（即从地球中心到表面的距离）会有所不同。
       *  在赤道（0°纬度）处，半径最大，而在极点（90°或-90°纬度）处，半径为零。
       * 2. 余弦函数的应用
       *  余弦函数：在三角学中，余弦函数用于计算一个角所对应的直角三角形中邻边与斜边的比率。
       *  在这里，使用 `cos(lat)` 是因为纬度越高，地面上单位纬度对应的实际距离越小。通过计算该纬度的余弦值，可以得到该纬度对应的半径。
       * 3. 将纬度转化为弧度
       *  纬度以度数表示，但三角函数通常使用弧度。因此，公式中将纬度转换为弧度：`lat× (π/180)`
       * 4. 乘以地球半径
       *  sphereRadius 通常代表一个标准化的地球半径，例如 6371公里（地球平均半径）。通过将余弦值与该半径相乘，可以得到在特定纬度上的实际半径。
       * 5. 总结
       *  这个公式通过结合三角学和地理知识，计算出在特定纬度下，从地球中心到该纬度表面的距离（即半径），这对于进一步的地理计算，如距离测量等非常重要。
       *
       * 这是在计算地球上不同纬度的"圆圈"半径。想象一下：
       *  1. 在赤道（纬度0°）处，圆圈最大，半径等于球体半径
       *  2. 越往南北极走，圆圈越小
       *  3. 到了极点（纬度90°或-90°），圆圈就缩成了一个点
       * 就像切橙子：
       *  1. 从中间（赤道）切，得到最大的圆
       *  2. 往上下切，圆圈越来越小
       *  3. 到顶部和底部，就只有一个点了
       */
			const radius = Math.cos(Math.abs(lat) * (Math.PI / 180)) * this.options.sphereRadius;
      // 计算当前纬度的周长 = 2π * r
			const circumference = radius * Math.PI * 2;
      // 计算当前纬度的点数 = 周长 circumference * 密度 density
			const dotsForLat = circumference * this.options.density;

			for (let x = 0; x < dotsForLat; x++) {
        /**
         * 计算当前点的经度，公式原理解析
         *
         * 这是在计算每个点的经度位置：
         * 1. 从 -180° 开始（最西边）
         * 2. 到 +180° 结束（最东边）
         * 3. dotsForLat 是这个纬度上要放多少个点
         * 4. x 是当前的第几个点
         * 5. 通过这个公式，把点均匀分布在这个纬度圈上
         *
         * 就像在圆圈上均匀摆放珠子：
         * 1. 知道要摆放多少颗珠子(dotsForLat)
         * 2. 从起点 (-180°) 开始
         * 3. 计算每颗珠子应该放在哪个角度位置
         *
         * 这样组合起来，就能在球体表面创建均匀分布的点，形成地球的点状地图。
         */
				const long = -180 + (x * 360) / dotsForLat;
        // 判断是否应该在该位置创建点
				if (!this.shouldCreateDot(long, lat)) continue;
        // 计算当前点的球面位置
				const position = this.calcSphericalPosition(long, lat);
        // 创建点
				this.createDot(position, i);
			}
		}
	}

	/**
	 * 判断是否应该在该位置创建点
	 */
	private shouldCreateDot(lon: number, lat: number): boolean {
    // 获取当前纬度的经度数组
		const latPoints = this.activeLatLon.get(lat);
		// 如果当前纬度的经度数组为空，则返回 false
		if (!latPoints?.length) return false;
    /**
     * 找到与当前经度最接近的经度
     *  假设我们有一个纬度线上的几个点：
     *  已知点的经度：[-175°, -170°, -165°]
     *  我们要找位置 lon = -172°
     *
     * 这个算法会：
     *  计算每个已知点与-172°的距离（取绝对值）：
     *  |-175 - (-172)| = 3°
     *  |-170 - (-172)| = 2° ← 最小！
     *  |-165 - (-172)| = 7°
     *
     * 通过比较找出距离最小的点：
     *  3° > 2°，保留-170°
     *  2° < 7°，继续保留-170°
     * 最终返回-170°，因为它是最接近-172°的点
     *
     * 这就像在找最近的公交站：
     *  你在-172°的位置
     *  周围有三个公交站（-175°，-170°，-165°）
     *  算法帮你找出最近的那个站（-170°）
     *
     * 这样做的目的是为了确定在地球表面上某个位置是否应该显示点，通过找到最接近的已知点来做判断。
     */
		const closest = latPoints.reduce((prev, curr) =>
			Math.abs(curr - lon) < Math.abs(prev - lon) ? curr : prev
		);

    // 判断当前经度与最接近经度的差值是否小于 0.5
		return Math.abs(lon - closest) < 0.5;
	}

	/**
	 * 计算球面位置
	 */
	private calcSphericalPosition(lon: number, lat: number): THREE.Vector3 {
    // 计算当前点的经纬度
    // 90 - lat 表示当前纬度与北极的距离
    // (Math.PI / 180) 表示将角度转换为弧度
		const phi = (90 - lat) * (Math.PI / 180);
    // lon + 180 表示当前经度与格林尼治的距离
    // (Math.PI / 180) 表示将角度转换为弧度
		const theta = (lon + 180) * (Math.PI / 180);

    // 计算当前点的球面位置
		return new THREE.Vector3(
      // -(this.options.sphereRadius * Math.sin(phi) * Math.cos(theta)) 表示当前点在球面上的 x 坐标
			-(this.options.sphereRadius * Math.sin(phi) * Math.cos(theta)),
      // this.options.sphereRadius * Math.cos(phi) 表示当前点在球面上的 y 坐标
			this.options.sphereRadius * Math.cos(phi),
      // this.options.sphereRadius * Math.sin(phi) * Math.sin(theta) 表示当前点在球面上的 z 坐标
			this.options.sphereRadius * Math.sin(phi) * Math.sin(theta)
		);
	}

	/**
	 * 创建单个点
	 */
	private createDot(position: THREE.Vector3, timeValue: number): void {
    // 创建一个圆形几何体
		const geometry = new THREE.CircleGeometry(this.options.dotRadius, 5);
    // 让圆形几何体朝向球面坐标
		geometry.lookAt(position);
    // 将圆形几何体移动到球面坐标
		geometry.translate(position.x, position.y, position.z);

    // 创建一个点材质
		const material = this.createDotMaterial(timeValue);
    // 创建一个点
		const mesh = new THREE.Mesh(geometry, material);
    // 将点添加到场景中
		this.scene.add(mesh);
	}

	/**
	 * 创建点的材质
	 */
	private createDotMaterial(timeValue: number): THREE.ShaderMaterial {
    // 克隆基础材质
		const material = this.baseMaterial.clone();
    // 设置时间值
		material.uniforms.u_time.value = timeValue * Math.sin(Math.random());
    // 将材质添加到材质数组中
		this.materials.push(material);

		return material;
	}

	/**
	 * 更新点的动画
	 */
	public update(): void {
    // 更新每个点的动画
		this.materials.forEach(material => {
			material.uniforms.u_time.value += 0.03;
		});
	}

	/**
	 * 更新鼠标位置
	 */
	public onMouseDown(): void {
    // 更新每个点的动画
		this.materials.forEach(material => {
			gsap.to(material.uniforms.u_maxExtrusion, {
				value: 1.01,
				duration: 0.3
			});
		});
	}

	/**
	 * 处理鼠标抬起事件
	 */
	public onMouseUp(): void {
    // 更新每个点的动画
		this.materials.forEach(material => {
			gsap.to(material.uniforms.u_maxExtrusion, {
				value: 1.0,
				duration: 0.15
			});
		});
	}

	/**
	 * 销毁点状地图
	 */
	public dispose(): void {
    // 销毁每个点材质
		this.materials.forEach(material => {
			material.dispose();
		});
    // 清空材质数组
		this.materials = [];
    // 清空纬度经度映射
		this.activeLatLon.clear();
	}
}
